import React from "react";
import "./App.css";
import "./fp-ui.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/SupabaseAuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { SoundProvider } from './contexts/SoundContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { DiscreteProvider } from './contexts/DiscreteContext';
import { RequireAuth } from './components/RequireAuth';
import Home from './pages/Home';
import ComicViewer from './pages/ComicViewer';
import FreeComicViewer from './pages/FreeComicViewer';
import News from './pages/News';
import MyCollection from './pages/MyCollection';
import InfoCredits from './pages/InfoCredits';
import Updates from './pages/Updates';
import Commissions from './pages/Commissions';
import CommissionsGallery from './pages/CommissionsGallery';
import LanguageSelect from './pages/LanguageSelect';
import Settings from './pages/Settings';
import WaBatch from './pages/WaBatch';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Redeem from './pages/Redeem';
import ResetPassword from './pages/ResetPassword';

function AppContent() {
  const { hasSelectedLanguage, isLoading } = useLanguage();
  const { isPasswordRecovery } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#1b0a33',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <img src="/logo.png" alt="FootParadise" className="fp-pulse-soft" style={{ height: 34, maxWidth: '70%', objectFit: 'contain' }} />
        <div className="fp-bar" style={{ width: 150, height: 14 }}><div className="fp-bar-fill fp-bar-fill--unknown" /></div>
      </div>
    );
  }

  // Show language selection if not selected yet
  if (!hasSelectedLanguage) {
    return <LanguageSelect />;
  }

  // A "reset password" email link lands here with a valid session but no
  // way to set a new password yet — take over the whole screen until they do.
  if (isPasswordRecovery) {
    return <ResetPassword />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/" element={<RequireAuth><Home /></RequireAuth>} />
        <Route path="/comic/:id" element={<RequireAuth><ComicViewer /></RequireAuth>} />
        <Route path="/free-comic/:id" element={<RequireAuth><FreeComicViewer /></RequireAuth>} />
        <Route path="/my-collection" element={<RequireAuth><MyCollection /></RequireAuth>} />
        <Route path="/redeem" element={<RequireAuth><Redeem /></RequireAuth>} />
        <Route path="/news" element={<News />} />
        <Route path="/info" element={<InfoCredits />} />
        <Route path="/updates" element={<Updates />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/commissions-gallery" element={<CommissionsGallery />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/wabatch" element={<WaBatch />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <LanguageProvider>
      {/* AuthProvider wraps the preference providers below so they can key
          their storage by the logged-in account (see each context's file) —
          this keeps dark mode / discrete mode / sound from bleeding between
          accounts sharing the same browser (e.g. Master). */}
      <AuthProvider>
        <DarkModeProvider>
          <DiscreteProvider>
            <SoundProvider>
              <AppContent />
            </SoundProvider>
          </DiscreteProvider>
        </DarkModeProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
